import React, { useMemo, useState, lazy, Component } from 'react';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { Search, ExternalLink, Phone, MapPin, Info, X, Crosshair, Target, Shield, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { gunbrokerListings, GUNBROKER_SELLER_URL, type GunBrokerListing } from '../constants/gunbrokerListings';
import { PHONE, PHONE_TEL, GOOGLE_MAPS_URL } from '../constants/business';
type PriceRange = 'All' | 'Under $250' | '$250–$500' | '$500–$1,000' | '$1,000–$2,500' | '$2,500+';
const CATEGORIES = ['All', 'Firearms', 'Ammunition', 'NFA / Suppressors'];
const ITEM_TYPES = ['All', 'Rifle', 'Shotgun', 'Handgun', 'Ammunition', 'Suppressor'];
const MANUFACTURERS = ['All', 'Winchester', 'Hornady', 'Browning', 'Savage', 'Weatherby', 'SilencerCo', 'Mossberg', 'Barrett'];
const CONDITIONS = ['All', 'New', 'Used', 'Not listed'];
const LISTING_TYPES = ['All', 'Fixed Price', 'Auction'];
const CALIBERS = ['All', '17 WSM', '223 Rem', '22 LR', '20 gauge', '308 Win', '17 HMR', '224 Wby Mag', 'Multi-caliber / 46M', '6.5 Creedmoor', '12 gauge', '50 BMG'];
const PRICE_RANGES: PriceRange[] = ['All', 'Under $250', '$250–$500', '$500–$1,000', '$1,000–$2,500', '$2,500+'];
const SORT_OPTIONS = ['Featured', 'Price low to high', 'Price high to low', 'Manufacturer A–Z', 'Title A–Z', 'New first', 'Used first', 'Ending soonest', 'Highest price', 'Lowest price'];
const QUICK_CHIPS: {
  label: string;
  tag: string;
}[] = [{
  label: 'New',
  tag: 'new'
}, {
  label: 'Used',
  tag: 'used'
}, {
  label: 'Ammunition',
  tag: 'ammunition'
}, {
  label: 'Shotguns',
  tag: 'shotgun'
}, {
  label: 'Rifles',
  tag: 'rifle'
}, {
  label: 'Ending Soon',
  tag: 'ending-soon'
}, {
  label: 'Free Shipping Available',
  tag: 'free-shipping'
}, {
  label: 'NFA / Suppressors',
  tag: 'nfa'
}];
function parseTimeLeft(value: string): number {
  if (!value) return Number.POSITIVE_INFINITY;
  const days = value.match(/(\d+)\s*d/i);
  const hours = value.match(/(\d+)\s*h/i);
  return (days ? parseInt(days[1], 10) * 24 : 0) + (hours ? parseInt(hours[1], 10) : 0);
}
function priceMatchesRange(price: number, range: PriceRange): boolean {
  switch (range) {
    case 'All':
      return true;
    case 'Under $250':
      return price < 250;
    case '$250–$500':
      return price >= 250 && price < 500;
    case '$500–$1,000':
      return price >= 500 && price < 1000;
    case '$1,000–$2,500':
      return price >= 1000 && price < 2500;
    case '$2,500+':
      return price >= 2500;
  }
}
const CATEGORY_PLACEHOLDERS: Record<GunBrokerListing['category'], {
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
}> = {
  Firearms: {
    label: 'Firearm Listing',
    icon: Crosshair
  },
  Ammunition: {
    label: 'Ammunition Listing',
    icon: Target
  },
  'NFA / Suppressors': {
    label: 'NFA Listing',
    icon: Shield
  }
};
function CategoryPlaceholder({
  category
}: {
  category: GunBrokerListing['category'];
}) {
  const {
    label,
    icon: Icon
  } = CATEGORY_PLACEHOLDERS[category];
  return <div className="relative w-full h-full bg-brand-cream flex flex-col items-center justify-center text-center px-4 overflow-hidden" data-id="element-550">
      {/* subtle corner accents */}
      <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-brand-hunter/20" data-id="element-551"></div>
      <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-brand-hunter/20" data-id="element-552"></div>

      <div className="w-14 h-14 rounded-full bg-brand-hunter flex items-center justify-center mb-3 shadow-sm" data-id="element-553">
        <Icon className="w-7 h-7 text-brand-offwhite" data-id="element-554" />
      </div>
      <span className="text-sm font-serif text-brand-charcoal" data-id="element-555">{label}</span>
      <span className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 mt-1" data-id="element-556">
        Kjergaard Sports · GunBroker preview
      </span>
    </div>;
}
function ListingCard({
  listing
}: {
  listing: GunBrokerListing;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = !!listing.imageUrl && !imgFailed;
  const conditionStyles = listing.condition === 'New' ? 'bg-brand-hunter text-brand-offwhite' : listing.condition === 'Used' ? 'bg-brand-leather text-brand-offwhite' : 'bg-brand-charcoal/80 text-brand-offwhite';
  const listingTypeStyles = listing.listingType === 'Auction' ? 'bg-brand-bone text-brand-leather border-brand-leather/40' : 'bg-brand-bone text-brand-hunter border-brand-hunter/40';
  return <article className="flex flex-col bg-brand-offwhite border border-brand-bone rounded-sm overflow-hidden hover:shadow-md hover:border-brand-leather/30 transition-all" data-id="element-557">
      {/* Image / placeholder */}
      <div className="relative h-48 bg-brand-offwhite border-b border-brand-bone" data-id="element-558">
        {showImage ? <img src={listing.imageUrl} alt={listing.title} loading="lazy" onError={() => setImgFailed(true)} className="absolute inset-0 w-full h-full object-contain p-3" data-id="element-559" /> : <CategoryPlaceholder category={listing.category} data-id="element-560" />}

        <div className="absolute top-3 left-3 flex gap-1.5 z-10" data-id="element-561">
          <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${conditionStyles}`} data-id="element-562">
            {listing.condition}
          </span>
          <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${listingTypeStyles}`} data-id="element-563">
            {listing.listingType}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col p-5" data-id="element-564">
        <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-wider text-brand-hunter font-bold" data-id="element-565">
          <span data-id="element-566">{listing.manufacturer}</span>
          <span className="text-brand-charcoal/40" data-id="element-567">·</span>
          <span className="text-brand-charcoal/80" data-id="element-568">{listing.itemType}</span>
        </div>

        <h3 className="text-base md:text-lg font-serif text-brand-charcoal leading-snug mb-3 line-clamp-2" data-id="element-569">
          {listing.title}
        </h3>

        {/* Specs */}
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[13px] mb-4" data-id="element-570">
          {listing.model && <>
              <dt className="text-brand-charcoal/70 font-medium" data-id="element-571">Model</dt>
              <dd className="text-brand-charcoal text-right truncate" data-id="element-572">
                {listing.model}
              </dd>
            </>}
          <dt className="text-brand-charcoal/70 font-medium" data-id="element-573">Caliber/Gauge</dt>
          <dd className="text-brand-charcoal text-right" data-id="element-574">
            {listing.caliberGauge}
          </dd>
          {listing.roundCount && <>
              <dt className="text-brand-charcoal/70 font-medium" data-id="element-575">Rounds</dt>
              <dd className="text-brand-charcoal text-right" data-id="element-576">
                {listing.roundCount}
                {listing.pricePerRound && <span className="text-brand-charcoal/60 ml-1" data-id="element-577">
                    ({listing.pricePerRound})
                  </span>}
              </dd>
            </>}
          {listing.quantity && <>
              <dt className="text-brand-charcoal/70 font-medium" data-id="element-578">Qty</dt>
              <dd className="text-brand-charcoal text-right" data-id="element-579">
                {listing.quantity}
              </dd>
            </>}
          {listing.shipping && <>
              <dt className="text-brand-charcoal/70 font-medium" data-id="element-580">Shipping</dt>
              <dd className="text-brand-charcoal text-right" data-id="element-581">
                {listing.shipping}
              </dd>
            </>}
          {listing.listingType === 'Auction' && <>
              {listing.bids !== '' && <>
                  <dt className="text-brand-charcoal/70 font-medium" data-id="element-582">Bids</dt>
                  <dd className="text-brand-charcoal text-right" data-id="element-583">
                    {listing.bids}
                  </dd>
                </>}
              {listing.timeLeft && <>
                  <dt className="text-brand-charcoal/70 font-medium" data-id="element-584">
                    Time left
                  </dt>
                  <dd className="text-brand-leather text-right font-semibold" data-id="element-585">
                    {listing.timeLeft}
                  </dd>
                </>}
            </>}
        </dl>

        {/* Price block */}
        <div className="mt-auto pt-4 border-t border-brand-bone" data-id="element-586">
          <div className="flex items-baseline justify-between mb-3" data-id="element-587">
            <span className="text-[10px] uppercase tracking-widest text-brand-charcoal/65 font-bold" data-id="element-588">
              {listing.listingType === 'Auction' ? 'Starting Bid' : 'Price'}
            </span>
            <span className="text-2xl font-serif text-brand-hunter font-semibold" data-id="element-589">
              {listing.priceLabel.replace(/^Starting Bid\s*/i, '')}
            </span>
          </div>

          <div className="flex flex-col gap-2 mb-3" data-id="element-590">
            <a href={listing.gunbrokerUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-4 py-2.5 rounded-sm font-semibold text-sm transition-colors shadow-sm" data-id="element-591">
              View on GunBroker
              <ExternalLink className="w-4 h-4 ml-2" data-id="element-592" />
            </a>
            <a href={PHONE_TEL} className="inline-flex items-center justify-center bg-brand-offwhite border border-brand-charcoal/20 hover:border-brand-leather hover:text-brand-leather text-brand-charcoal px-4 py-2 rounded-sm font-medium text-sm transition-colors" data-id="element-593">
              <Phone className="w-3.5 h-3.5 mr-2" data-id="element-594" />
              Call {PHONE}
            </a>
          </div>

          <div className="flex items-center justify-between text-[10px] text-brand-charcoal/55" data-id="element-595">
            <span data-id="element-596">Item #{listing.itemNumber}</span>
            <span className="italic" data-id="element-597">Subject to change on GunBroker</span>
          </div>
        </div>
      </div>
    </article>;
}
function SelectControl({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}) {
  return <label className="flex flex-col text-sm min-w-0" data-id="element-598">
      <span className="text-[10px] font-bold uppercase tracking-wider text-brand-charcoal/70 mb-1" data-id="element-599">
        {label}
      </span>
      <select value={value} onChange={e => onChange(e.target.value)} className="bg-brand-offwhite border border-brand-bone rounded-sm px-2.5 py-1.5 text-sm text-brand-charcoal focus:outline-none focus:border-brand-leather" data-id="element-600">
        {options.map(opt => <option key={opt} value={opt} data-id="element-601">
            {opt}
          </option>)}
      </select>
    </label>;
}
export function GunBrokerListings() {
  useDocumentMeta('GunBroker Listings | Kjergaard Sports', 'Browse selected Kjergaard Sports listings currently shown on GunBroker — firearms, ammunition, and NFA items. View pricing and current details on GunBroker, or call the store.');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [itemType, setItemType] = useState('All');
  const [manufacturer, setManufacturer] = useState('All');
  const [condition, setCondition] = useState('All');
  const [listingType, setListingType] = useState('All');
  const [caliber, setCaliber] = useState('All');
  const [priceRange, setPriceRange] = useState<PriceRange>('All');
  const [sort, setSort] = useState('Featured');
  const [quickFilter, setQuickFilter] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const results = gunbrokerListings.filter(l => {
      if (q) {
        const haystack = [l.title, l.manufacturer, l.model, l.caliberGauge, l.itemNumber, ...l.tags].join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (category !== 'All' && l.category !== category) return false;
      if (itemType !== 'All' && l.itemType !== itemType) return false;
      if (manufacturer !== 'All' && l.manufacturer !== manufacturer) return false;
      if (condition !== 'All' && l.condition !== condition) return false;
      if (listingType !== 'All' && l.listingType !== listingType) return false;
      if (caliber !== 'All' && l.caliberGauge !== caliber) return false;
      if (!priceMatchesRange(l.priceValue, priceRange)) return false;
      if (quickFilter) {
        switch (quickFilter) {
          case 'new':
            if (l.condition !== 'New') return false;
            break;
          case 'used':
            if (l.condition !== 'Used') return false;
            break;
          case 'ammunition':
            if (l.category !== 'Ammunition') return false;
            break;
          case 'shotgun':
            if (l.itemType !== 'Shotgun') return false;
            break;
          case 'rifle':
            if (l.itemType !== 'Rifle') return false;
            break;
          case 'ending-soon':
            if (parseTimeLeft(l.timeLeft) > 48) return false;
            break;
          case 'free-shipping':
            if (!/free/i.test(l.shipping)) return false;
            break;
          case 'nfa':
            if (l.category !== 'NFA / Suppressors') return false;
            break;
        }
      }
      return true;
    });
    const sorted = [...results];
    switch (sort) {
      case 'Price low to high':
      case 'Lowest price':
        sorted.sort((a, b) => a.priceValue - b.priceValue);
        break;
      case 'Price high to low':
      case 'Highest price':
        sorted.sort((a, b) => b.priceValue - a.priceValue);
        break;
      case 'Manufacturer A–Z':
        sorted.sort((a, b) => a.manufacturer.localeCompare(b.manufacturer));
        break;
      case 'Title A–Z':
        sorted.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'New first':
        sorted.sort((a, b) => (a.condition === 'New' ? 0 : 1) - (b.condition === 'New' ? 0 : 1));
        break;
      case 'Used first':
        sorted.sort((a, b) => (a.condition === 'Used' ? 0 : 1) - (b.condition === 'Used' ? 0 : 1));
        break;
      case 'Ending soonest':
        sorted.sort((a, b) => parseTimeLeft(a.timeLeft) - parseTimeLeft(b.timeLeft));
        break;
    }
    return sorted;
  }, [search, category, itemType, manufacturer, condition, listingType, caliber, priceRange, quickFilter, sort]);
  const advancedFilterCount = [category !== 'All', itemType !== 'All', manufacturer !== 'All', condition !== 'All', listingType !== 'All', caliber !== 'All', priceRange !== 'All'].filter(Boolean).length;
  const hasAnyAdjustment = search !== '' || quickFilter !== null || advancedFilterCount > 0 || sort !== 'Featured';
  const resetFilters = () => {
    setSearch('');
    setCategory('All');
    setItemType('All');
    setManufacturer('All');
    setCondition('All');
    setListingType('All');
    setCaliber('All');
    setPriceRange('All');
    setQuickFilter(null);
    setSort('Featured');
  };
  return <div data-id="element-602">
      {/* COMPACT TITLE BAND */}
      <section className="relative bg-brand-charcoal overflow-hidden" data-id="element-603">
        <div className="absolute inset-0 z-0" data-id="element-604">
          <img src="https://cdn.magicpatterns.com/uploads/h2HHsk76CJxRY2pmsyL8mj/firearms-wall.jpg" alt="" aria-hidden="true" className="w-full h-full object-cover object-center" data-id="element-605" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/90 via-brand-charcoal/70 to-brand-charcoal/55" data-id="element-606"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12" data-id="element-607">
          <div className="flex items-center space-x-3 mb-2" data-id="element-608">
            <div className="h-px w-8 bg-brand-leather" data-id="element-609"></div>
            <span className="text-brand-bone uppercase tracking-widest text-[11px] font-semibold" data-id="element-610">
              Kjergaard Sports · GunBroker Preview
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-serif text-brand-offwhite leading-tight" data-id="element-611">
            GunBroker Listings
          </h1>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="py-8 md:py-10 bg-brand-offwhite" data-id="element-612">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-613">
          {/* Compact intro + status */}
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5 mb-6" data-id="element-614">
            <div data-id="element-615">
              <p className="text-base text-brand-charcoal/85 leading-relaxed mb-2" data-id="element-616">
                Browse selected Kjergaard Sports listings currently shown on
                GunBroker. For current pricing, bidding, availability, terms,
                and final transaction details, view the item directly on
                GunBroker or call Kjergaard Sports.
              </p>
              <a href={GUNBROKER_SELLER_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm text-brand-hunter font-semibold hover:text-brand-wood transition-colors" data-id="element-617">
                View all Kjergaard Sports GunBroker listings
                <ExternalLink className="w-3.5 h-3.5 ml-1.5" data-id="element-618" />
              </a>
            </div>
            <div className="bg-brand-cream border-l-4 border-brand-leather p-4 rounded-sm" data-id="element-619">
              <div className="flex items-start" data-id="element-620">
                <Info className="w-4 h-4 text-brand-leather mr-2.5 flex-shrink-0 mt-0.5" data-id="element-621" />
                <p className="text-brand-charcoal/85 text-[13px] leading-relaxed" data-id="element-622">
                  Listings shown here are a selected preview and may not reflect
                  every current GunBroker item. Availability, price, and status
                  may change. All sales, transfers, and regulated services are
                  handled in accordance with applicable laws.
                </p>
              </div>
            </div>
          </div>

          {/* Filter bar */}
          <div className="bg-brand-cream border border-brand-bone rounded-sm p-4 md:p-5 mb-5" data-id="element-623">
            {/* Row 1: search + sort */}
            <div className="grid md:grid-cols-[1fr_auto_auto] gap-3 mb-3" data-id="element-624">
              <div className="relative" data-id="element-625">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-charcoal/50" data-id="element-626" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title, manufacturer, model, caliber, item number, or tags" className="w-full bg-brand-offwhite border border-brand-bone rounded-sm pl-10 pr-3 py-2.5 text-sm text-brand-charcoal focus:outline-none focus:border-brand-leather" data-id="element-627" />
              </div>
              <div className="flex items-center gap-2" data-id="element-628">
                <label className="flex items-center gap-2 text-sm" data-id="element-629">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-brand-charcoal/70 whitespace-nowrap" data-id="element-630">
                    Sort
                  </span>
                  <select value={sort} onChange={e => setSort(e.target.value)} className="bg-brand-offwhite border border-brand-bone rounded-sm px-2.5 py-2 text-sm text-brand-charcoal focus:outline-none focus:border-brand-leather" data-id="element-631">
                    {SORT_OPTIONS.map(opt => <option key={opt} value={opt} data-id="element-632">
                        {opt}
                      </option>)}
                  </select>
                </label>
              </div>
              <button type="button" onClick={() => setAdvancedOpen(v => !v)} className="inline-flex items-center justify-center bg-brand-offwhite border border-brand-bone hover:border-brand-leather text-brand-charcoal hover:text-brand-leather px-3 py-2 rounded-sm text-sm font-medium transition-colors" aria-expanded={advancedOpen} data-id="element-633">
                <SlidersHorizontal className="w-4 h-4 mr-2" data-id="element-634" />
                Advanced Filters
                {advancedFilterCount > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-brand-hunter text-brand-offwhite text-[11px] font-bold rounded-full" data-id="element-635">
                    {advancedFilterCount}
                  </span>}
                <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} data-id="element-636" />
              </button>
            </div>

            {/* Advanced filters (collapsible) */}
            {advancedOpen && <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-3 pb-3 border-t border-brand-bone" data-id="element-637">
                <SelectControl label="Category" value={category} options={CATEGORIES} onChange={setCategory} data-id="element-638" />
                <SelectControl label="Item type" value={itemType} options={ITEM_TYPES} onChange={setItemType} data-id="element-639" />
                <SelectControl label="Manufacturer" value={manufacturer} options={MANUFACTURERS} onChange={setManufacturer} data-id="element-640" />
                <SelectControl label="Condition" value={condition} options={CONDITIONS} onChange={setCondition} data-id="element-641" />
                <SelectControl label="Listing type" value={listingType} options={LISTING_TYPES} onChange={setListingType} data-id="element-642" />
                <SelectControl label="Caliber / Gauge" value={caliber} options={CALIBERS} onChange={setCaliber} data-id="element-643" />
                <SelectControl label="Price range" value={priceRange} options={PRICE_RANGES} onChange={v => setPriceRange(v as PriceRange)} data-id="element-644" />
              </div>}

            {/* Quick chips */}
            <div className="flex items-center flex-wrap gap-2 pt-3 border-t border-brand-bone" data-id="element-645">
              <span className="text-[10px] font-bold uppercase tracking-wider text-brand-charcoal/70 mr-1" data-id="element-646">
                Quick:
              </span>
              {QUICK_CHIPS.map(chip => {
              const active = quickFilter === chip.tag;
              return <button key={chip.tag} type="button" onClick={() => setQuickFilter(active ? null : chip.tag)} className={`px-2.5 py-1 rounded-sm text-[12px] font-medium border transition-colors ${active ? 'bg-brand-hunter border-brand-hunter text-brand-offwhite' : 'bg-brand-offwhite border-brand-bone text-brand-charcoal hover:border-brand-leather hover:text-brand-leather'}`} data-id="element-647">
                    {chip.label}
                  </button>;
            })}
            </div>
          </div>

          {/* Results summary */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5" data-id="element-648">
            <p className="text-sm text-brand-charcoal/80" data-id="element-649">
              <strong className="text-brand-charcoal" data-id="element-650">{filtered.length}</strong>{' '}
              of {gunbrokerListings.length} listings
            </p>
            {hasAnyAdjustment && <button type="button" onClick={resetFilters} className="inline-flex items-center text-sm text-brand-leather hover:text-brand-hunter font-semibold" data-id="element-651">
                <X className="w-4 h-4 mr-1" data-id="element-652" />
                Clear filters
              </button>}
          </div>

          {/* Listings */}
          {filtered.length === 0 ? <div className="bg-brand-cream border border-brand-bone rounded-sm p-10 text-center max-w-2xl mx-auto" data-id="element-653">
              <Info className="w-9 h-9 text-brand-leather mx-auto mb-3" data-id="element-654" />
              <h3 className="text-lg font-serif text-brand-charcoal mb-2" data-id="element-655">
                No listings match your filters
              </h3>
              <p className="text-brand-charcoal/75 leading-relaxed mb-5 text-sm" data-id="element-656">
                Try adjusting your search or view all Kjergaard Sports GunBroker
                listings.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-657">
                <button type="button" onClick={resetFilters} className="inline-flex items-center justify-center border border-brand-charcoal/20 hover:border-brand-leather hover:text-brand-leather text-brand-charcoal px-5 py-2 rounded-sm font-medium text-sm" data-id="element-658">
                  Clear filters
                </button>
                <a href={GUNBROKER_SELLER_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-5 py-2 rounded-sm font-medium text-sm" data-id="element-659">
                  View All on GunBroker
                  <ExternalLink className="w-4 h-4 ml-2" data-id="element-660" />
                </a>
              </div>
            </div> : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-id="element-661">
              {filtered.map(listing => <ListingCard key={listing.itemNumber} listing={listing} data-id="element-662" />)}
            </div>}
        </div>
      </section>

      {/* Single bottom CTA */}
      <section className="py-16 bg-brand-hunter text-brand-offwhite" data-id="element-663">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-664">
          <h2 className="text-3xl md:text-4xl font-serif mb-5" data-id="element-665">
            Questions About a Listing?
          </h2>
          <p className="text-base md:text-lg text-brand-offwhite/85 mb-8 leading-relaxed" data-id="element-666">
            Call Kjergaard Sports with questions about availability, transfers,
            or visiting the store. For current auction status and pricing, use
            the GunBroker listing link.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-667">
            <a href={PHONE_TEL} className="bg-brand-leather hover:bg-brand-wood text-brand-offwhite px-6 py-3 rounded-sm font-semibold transition-colors inline-flex items-center justify-center" data-id="element-668">
              <Phone className="w-5 h-5 mr-2" data-id="element-669" />
              Call {PHONE}
            </a>
            <a href={GUNBROKER_SELLER_URL} target="_blank" rel="noopener noreferrer" className="border border-brand-offwhite/40 hover:border-brand-offwhite text-brand-offwhite px-6 py-3 rounded-sm font-semibold transition-colors inline-flex items-center justify-center" data-id="element-670">
              <ExternalLink className="w-5 h-5 mr-2" data-id="element-671" />
              View All GunBroker Listings
            </a>
            <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="border border-brand-offwhite/40 hover:border-brand-offwhite text-brand-offwhite px-6 py-3 rounded-sm font-semibold transition-colors inline-flex items-center justify-center" data-id="element-672">
              <MapPin className="w-5 h-5 mr-2" data-id="element-673" />
              Get Directions
            </a>
          </div>
        </div>
      </section>
    </div>;
}